<template>
  <nav class="header-navigation__wrapper" ref="navigation">
    <ul class="header-navigation__list">
      <li
        v-for="(parentItem, index) in navigationItemsWithMoreElementCloned"
        :key="`${parentItem.Name}${navigationItemRenderKeys[index]}`"
        :id="parentItem.Id"
        :style="getNavigationListItemDynamicStyle(index)"
        class="header-navigation__list-item"
        :class="getNavigationItemClass(index)"
        :ref="generateWrapperChildRefName(index)"
        @touchstart="navigation.onListItemTouchstart($event)"
        @click="navigation.onListItemClick($event, index)"
        @mouseenter="navigation.onListItemMouseEnter($event, index)"
        @mouseleave="navigation.onListItemMouseLeave($event, index)"
      >
        <div
          class="header-navigation__list-item-link-wrapper"
          :class="dynamicActiveClass(parentItem)"
          v-bind="parentItem.attrs"
        >
          <a-icon
            v-if="parentItem.Icon"
            :icon="parentItem.Icon"
            :width="16"
            :height="16"
            :original="true"
            :no-hover="true"
            class="header-navigation__list-item-icon"
          />
          <a-link
            :to="parentItem.Link"
            :trailing-slash="$helper.isLinkWithTrailingSlash(parentItem.Link)"
            class="header-navigation__list-link"
            tabindex="0"
            @focus="navigation.onLinkFocus($event, index)"
            @blur="navigation.onLinkBlur($event, index)"
            @click="navigation.onLinkClick($event, index)"
          >
            {{ parentItem.Name }}
          </a-link>
          <template v-if="isDropdownArrowVisible(parentItem)">
            <a-icon
              :icon="$options.consts.ICON.ARROW_DOWN_PURPLE"
              :width="16"
              :height="16"
              :original="true"
              :no-hover="true"
              class="header-navigation__arrow"
            />
            <a-header-navigation-submenu
              class="header-navigation__submenu"
              :items="parentItem.ChildItems"
              @item-click="navigation.onSubMenuClick()"
              @blur="navigation.onSubMenuBlur($event, index)"
            />
          </template>
        </div>
      </li>
    </ul>
  </nav>
</template>

<script>
import mixins from '@/utils/mixins'
import AHeaderNavigationSubmenu from './AHeaderNavigationSubmenu'
import AIcon, { ICON } from 'shared/AIcon'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import deepClone from 'shared/AMultiselect/utils'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { FONT } from 'shared/ACharWidth/enums'
import { HEADER_SUBMENU_ITEM_CLASS } from '@/components/AHeader/AHeaderDesktop/enums'
import { Navigation } from '@/utils/helpers/navigation'
import { HEADER_ITEM_ID } from '@/components/AHeader/enums'
import { HEADER_MENU_ITEM_MORE } from '@/components/AHeader/AHeaderDesktop/AHeaderNavigation/enums'

const ARROW_WIDTH = 21 // icon + margin-left
const ICON_WIDTH = 22 // icon + margin-right
const LIST_ITEM_PADDING_DESKTOP = 16
const LIST_ITEM_PADDING_DESKTOP_SM = 16
const LIST_ITEM_PADDING_TABLET = 16

const SUBMENU_WIDTH = 192
const SUBMENU_COUNT_REGULAR = 2
const SUBMENU_COUNT_MAX = 3

const DROPDOWN_CLASS = {
  OPENED: 'opened',
  DIRECTION_LEFT: 'left',
  DIRECTION_RIGHT: 'right'
}

export default {
  name: 'AHeaderNavigation',
  mixins: [mixins.refDimensions, hydrationHelpers],
  components: { AIcon, AHeaderNavigationSubmenu },
  props: {
    navigationItems: propValidator([PROP_TYPES.ARRAY])
  },
  consts: {
    ICON
  },
  data() {
    const itemsLengthWithMoreItem = this.navigationItems.length + 1

    return {
      listItemsWidthValues: null,
      currentlyMovedToDropdown: [],
      navigationItemsWithMoreElementCloned: null,
      navigationItemClasses: new Array(itemsLengthWithMoreItem).fill(''),
      navigationItemRenderKeys: new Array(itemsLengthWithMoreItem).fill(''),
      navigation: new Navigation({
        onMouseHover: this.openDropdown.bind(this),
        onMouseAway: this.closeDropdown.bind(this),
        onTextClick: this.onClick.bind(this),
        onTouchArrowClick: this.openDropdown.bind(this),
        onTabFocus: this.openDropdown.bind(this),
        onBlur: this.onNavigationItemBlur.bind(this),
        onSubMenuClick: this.closeDropdowns.bind(this),
        onSubMenuBlur: this.onNavigationItemBlur.bind(this)
      })
    }
  },
  computed: {
    moreMenuItemWidth() {
      return this.getNavItemWidthValue(HEADER_MENU_ITEM_MORE)
    },
    moreMenuItemWidthWithPadding() {
      return this.moreMenuItemWidth + this.currentSidePaddingValueForItem
    },
    isMoreItemVisible() {
      return !!this.currentlyMovedToDropdown.length
    },
    navigationItemsWithMoreElement() {
      return [...this.navigationItems, HEADER_MENU_ITEM_MORE]
    },
    navigationItemsLength() {
      return this.navigationItemsWithMoreElementCloned.length
    },
    lastNavigationItem() {
      return this.navigationItemsWithMoreElementCloned[
        this.navigationItemsLength - 1
      ]
    },
    currentSidePaddingValueForItem() {
      return this.$_hydrationHelpers_getValueByBreakpoint({
        [this.$breakpoint.aboveDesktopXl]: LIST_ITEM_PADDING_DESKTOP,
        [this.$breakpoint.desktopSm]: LIST_ITEM_PADDING_DESKTOP_SM,
        [this.$breakpoint.tablet]: LIST_ITEM_PADDING_TABLET
      })
    }
  },
  watch: {
    $_hydrationHelpers_windowWidth(newVal, oldVal) {
      /**
       * Due to the fact that setting window width in store at the first page load
       * is immediately treated as a width change, the watcher is triggered on init.
       * This is not a desirable effect since in this case "setResultingNavigationItems"
       * might be called concurrently (mounted + watcher) and this may lead to unexpected
       * results
       */
      if (!oldVal) return

      this.closeDropdowns()
      this.setResultingNavigationItems()
    },
    navigationItems: {
      async handler() {
        this.cloneNavigationItems()
        this.currentlyMovedToDropdown = []
        this.calculateListItemsWidthValues()
        this.setResultingNavigationItems()
        this.handleMoreItemDisplay()
      },
      deep: true
    }
  },
  methods: {
    isDropdownArrowVisible(item) {
      return item.ChildItems && item.ChildItems.length
    },
    isChildMenuItem(el) {
      if (!el || !el.classList) return false

      return el.classList.contains(HEADER_SUBMENU_ITEM_CLASS)
    },
    onNavigationItemBlur(event, index) {
      if (this.isChildMenuItem(event.relatedTarget)) {
        return
      }

      this.closeDropdown(event, index)
    },
    generateWrapperChildRefName(index) {
      /**
       * We add "-index" to ref to avoid weird bug when focusing on one dropdown
       * while hovering on another leads to incorrect open direction of dropdown
       * due to unexpected change in ref element by index
       */
      return `navigationItemWrapper-${index}`
    },
    getItemWrapperChildRefByIndex(index) {
      return this.$refs[this.generateWrapperChildRefName(index)][0]
    },
    closeActiveDropdown() {
      const dropdownToCloseIndex = this.navigationItemClasses.findIndex(v => v)

      this.$set(this.navigationItemClasses, dropdownToCloseIndex, '')
    },
    onClick(index) {
      /**
       * If the item is a link, navigate to it.
       */
      if (this.navigationItemsWithMoreElementCloned[index].Link) {
        this.closeDropdowns()
        /**
         * Otherwise, open a dropdown menu (i.e. the "More" item).
         * Used on touch.
         */
      } else {
        this.openDropdown(index)
      }
    },
    async openDropdown(index) {
      this.closeActiveDropdown()

      const { ChildItems } = this.navigationItemsWithMoreElementCloned[index]
      if (!ChildItems || !ChildItems.length) return

      // calculating whether to open dropdowns to left or right depending on index and
      // position
      const leftOffset = this.getItemWrapperChildRefByIndex(
        index
      ).getBoundingClientRect().left
      const windowWidthWithoutScrollbar = document.body.clientWidth

      // last item is the only item that can include 3 dropdowns
      const sumOfDropdownsWidth = this.checkIfMoreMenuItem(index)
        ? SUBMENU_WIDTH * SUBMENU_COUNT_MAX
        : SUBMENU_WIDTH * SUBMENU_COUNT_REGULAR

      const opensToRight =
        leftOffset + sumOfDropdownsWidth < windowWidthWithoutScrollbar

      const resultingDirectionClass = opensToRight
        ? DROPDOWN_CLASS.DIRECTION_RIGHT
        : DROPDOWN_CLASS.DIRECTION_LEFT

      this.$set(
        this.navigationItemClasses,
        index,
        `${DROPDOWN_CLASS.OPENED} ${resultingDirectionClass}`
      )
    },
    closeDropdowns() {
      this.navigationItemClasses = new Array(
        this.navigationItemsWithMoreElement.length
      ).fill('')
    },
    closeDropdown(event, index) {
      this.$set(this.navigationItemClasses, index, '')
    },
    getNavigationItemClass(index) {
      return this.navigationItemClasses[index]
    },
    getNavigationListItemDynamicStyle(index) {
      // excluding first child
      const paddingLeft = index === 0 ? 0 : this.currentSidePaddingValueForItem

      // excluding last child
      const paddingRight = this.checkIfLastVisibleMenuItem(index)
        ? 0
        : this.currentSidePaddingValueForItem

      return {
        padding: `0 ${paddingRight}px 5px ${paddingLeft}px`
      }
    },
    getSumOfNavItemsWidth() {
      if (!this.$_hydrationHelpers_windowWidth) {
        return
      }

      const paddingSumValue =
        (this.navigationItemsWithMoreElementCloned.length - 1) *
        this.currentSidePaddingValueForItem *
        2

      const sum = this.navigationItemsWithMoreElementCloned.reduce(
        (acc, navItem) => {
          return acc + this.getNavItemWidthValue(navItem)
        },
        paddingSumValue
      )

      return this.isMoreItemVisible
        ? sum
        : this.subtractMoreItemFromItemsWidthSum(sum)
    },
    subtractMoreItemFromItemsWidthSum(sum) {
      /**
       * Important: when subtracting the "More" item, we have to also subtract
       * an extra padding from the adjacent element
       */
      return (
        sum -
        this.moreMenuItemWidthWithPadding -
        this.currentSidePaddingValueForItem
      )
    },
    addMoreItemFromItemsWidthSum(sum) {
      /**
       * Important: when adding the "More" item, we have to also add
       * an extra padding to compensate for the adjacent element
       */
      return (
        sum +
        this.moreMenuItemWidthWithPadding +
        this.currentSidePaddingValueForItem
      )
    },
    getCountOfItemsToMoveToDropdown(wrapperWidth, sumOfNavItemsWidth) {
      return this.navigationItemsWithMoreElementCloned.reduceRight(
        (acc, el, index) => {
          /**
           * Special menu item cannot be moved
           */
          if (this.checkIfMoreMenuItem(index)) {
            return acc
          }

          /**
           * First element with only paddingRight is not taken into account
           * (because it will never be the case according to the design)
           */
          const targetItemWidth =
            this.listItemsWidthValues[index] +
            2 * this.currentSidePaddingValueForItem

          if (acc.currentMenuItemsWidthSum > wrapperWidth) {
            /**
             * Here we need to consider a case when "More" item will replace
             * an existing item(s).
             * Consideration: we need to make sure there is enough space for the
             * "More" itself. If not, we add more items to it.
             */
            if (!this.isMoreItemVisible && !acc.moveCount) {
              acc.currentMenuItemsWidthSum = this.addMoreItemFromItemsWidthSum(
                acc.currentMenuItemsWidthSum
              )
            }

            this.currentlyMovedToDropdown.push({
              ...el,
              originalIndex: index
            })
            acc.currentMenuItemsWidthSum -= targetItemWidth
            acc.moveCount++
          }

          return acc
        },
        {
          moveCount: 0,
          currentMenuItemsWidthSum: sumOfNavItemsWidth
        }
      ).moveCount
    },
    getCountOfItemsToMoveFromDropdown(wrapperWidth, sumOfNavItemsWidth) {
      const {
        moveCount,
        moveCountWithoutMoreItem
      } = this.currentlyMovedToDropdown.reduceRight(
        (acc, el) => {
          /**
           * First element with only paddingRight is not taken into account
           * (because it will never be the case according to the design)
           */
          const targetItemWidth =
            this.listItemsWidthValues[el.originalIndex] +
            2 * this.currentSidePaddingValueForItem

          if (acc.currentMenuItemsWidthSum + targetItemWidth <= wrapperWidth) {
            acc.moveCount++
          }

          if (
            acc.currentMenuItemsWidthSumWithoutMore + targetItemWidth <=
            wrapperWidth
          ) {
            acc.moveCountWithoutMoreItem++
          }

          acc.currentMenuItemsWidthSum += targetItemWidth
          acc.currentMenuItemsWidthSumWithoutMore += targetItemWidth

          return acc
        },
        {
          moveCount: 0,
          moveCountWithoutMoreItem: 0,
          currentMenuItemsWidthSum: sumOfNavItemsWidth,
          currentMenuItemsWidthSumWithoutMore: this.subtractMoreItemFromItemsWidthSum(
            sumOfNavItemsWidth
          )
        }
      )

      /**
       * Here we need to consider a corner case when hiding the "More" item
       * frees enough space for item(s) that will replace it
       */
      const resultingNumberOfItemsToPop =
        this.currentlyMovedToDropdown.length === moveCountWithoutMoreItem
          ? moveCountWithoutMoreItem
          : moveCount

      this.popNumberOfItemsFromDropdown(resultingNumberOfItemsToPop)

      return resultingNumberOfItemsToPop
    },
    popNumberOfItemsFromDropdown(number) {
      for (let i = 1; i <= number; i++) {
        this.currentlyMovedToDropdown.pop()
      }
    },
    /**
     * The "More" element in the menu is considered a special case. This is the element
     * where other menu items are moved to/from when resizing
     */
    checkIfMoreMenuItem(index) {
      if (index == null) return false

      return (
        this.navigationItemsWithMoreElementCloned[index].Id ===
        HEADER_ITEM_ID.MORE
      )
    },
    checkIfLastVisibleMenuItem(index) {
      if (index == null) return false

      return this.isMoreItemVisible
        ? index === this.navigationItemsWithMoreElementCloned.length - 1
        : index === this.navigationItemsWithMoreElementCloned.length - 2
    },
    async setResultingNavigationItems() {
      await this.$nextTick()

      if (!this.listItemsWidthValues) return

      const sumOfNavItemsWidth = this.getSumOfNavItemsWidth()

      if (!sumOfNavItemsWidth) return

      if (!this.refDimensions.navigation) return

      const { width: wrapperWidth } = this.refDimensions.navigation

      if (sumOfNavItemsWidth <= wrapperWidth && !this.isMoreItemVisible) {
        return
      }

      if (sumOfNavItemsWidth > wrapperWidth) {
        const itemsToMoveCount = this.getCountOfItemsToMoveToDropdown(
          wrapperWidth,
          sumOfNavItemsWidth
        )

        if (!itemsToMoveCount) return

        this.moveItemsToDropdown(itemsToMoveCount)
      } else {
        const itemsToMoveCount = this.getCountOfItemsToMoveFromDropdown(
          wrapperWidth,
          sumOfNavItemsWidth
        )

        if (!itemsToMoveCount) return

        this.moveItemsFromDropdown(itemsToMoveCount)
      }
    },
    moveItemsFromDropdown(itemCount) {
      const itemsToMove = this.lastNavigationItem.ChildItems.slice(0, itemCount)

      this.navigationItemsWithMoreElementCloned = [
        ...this.navigationItemsWithMoreElementCloned.slice(
          0,
          this.navigationItemsWithMoreElementCloned.length - 1
        ),
        ...itemsToMove,
        {
          ...this.lastNavigationItem,
          ChildItems: [...this.lastNavigationItem.ChildItems.slice(itemCount)]
        }
      ]

      this.handleMoreItemDisplay()
    },
    moveItemsToDropdown(itemCount) {
      const itemsToMove = this.navigationItemsWithMoreElementCloned.slice(
        this.navigationItemsLength - itemCount - 1,
        this.navigationItemsLength - 1
      )

      this.navigationItemsWithMoreElementCloned = [
        ...this.navigationItemsWithMoreElementCloned.slice(
          0,
          this.navigationItemsWithMoreElementCloned.length - itemCount - 1
        ),
        {
          ...this.lastNavigationItem,
          ChildItems: [...itemsToMove, ...this.lastNavigationItem.ChildItems]
        }
      ]

      this.handleMoreItemDisplay()
    },
    handleMoreItemDisplay() {
      const moreListItemEl = document.getElementById(HEADER_ITEM_ID.MORE)

      if (this.isMoreItemVisible) {
        moreListItemEl.style.display = 'flex'
      } else {
        moreListItemEl.style.display = 'none'
      }
    },
    getNavItemWidthValue(navItem) {
      const textWidth = this.$helper.getTextWidth(
        navItem.Name,
        navItem.CustomFont || FONT.INTER_16_BOLD,
        false
      )

      const arrowWidth =
        !navItem.HideArrow && navItem.ChildItems.length ? ARROW_WIDTH : 0
      const iconWidth = navItem.Icon ? ICON_WIDTH : 0

      return textWidth + arrowWidth + iconWidth
    },
    calculateListItemsWidthValues() {
      this.listItemsWidthValues = this.navigationItemsWithMoreElementCloned.map(
        navItem => this.getNavItemWidthValue(navItem)
      )
    },
    cloneNavigationItems() {
      this.navigationItemsWithMoreElementCloned = deepClone(
        this.navigationItemsWithMoreElement
      )
    },
    formatRoutePaths(route) {
      return this.$helper.removeEndingSlashes(route.toLowerCase())
    },
    dynamicActiveClass(navigationItem) {
      if (!navigationItem.Link) return

      return {
        active:
          this.formatRoutePaths(this.$route.path) ===
          this.formatRoutePaths(
            this.$router.resolve(navigationItem.Link).resolved.path
          )
      }
    }
  },
  created() {
    this.cloneNavigationItems()
  },
  async mounted() {
    await this.$nextTick()

    this.calculateListItemsWidthValues()
    this.setResultingNavigationItems()
    this.handleMoreItemDisplay()
  }
}
</script>

<style lang="scss">
$header-submenu-border-radius: 4px;

.header-navigation__wrapper {
  position: relative;
  max-width: $desktop-full-hd-container;

  .header-navigation-submenu__wrapper {
    border-radius: $header-submenu-border-radius;
  }

  .header-navigation__list {
    width: 100%;
    height: 100%;
    list-style: none;
    display: flex;
    padding-bottom: 8px;
  }

  #more-menu-item {
    .header-navigation__list-link {
      // Make sure to update HEADER_MENU_ITEM_MORE (CustomFont) if the font changes
      // as there are calculations that depend on it
      font-size: 14px;
    }

    & > .header-navigation__list-item-link-wrapper > .header-navigation__arrow {
      // "More" item uses different icon
      display: none;
    }
  }

  .header-navigation__list-item {
    position: relative;
    display: flex;
    align-items: center;
    cursor: pointer;

    /* required to perform JS calculations */
    white-space: nowrap;

    &::after {
      display: none;
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-bottom: 9px solid black;
    }

    &:first-child {
      padding-left: 0;
    }

    &.opened {
      .header-navigation__submenu {
        display: block;
      }
    }

    &.right {
      .header-navigation__submenu {
        left: 0;
      }

      .header-navigation__submenu::before {
        left: 20px;
      }

      .header-navigation-submenu__nested {
        // to compensate for border-radius
        margin-left: -#{$header-submenu-border-radius * 2};
      }
    }

    &.left {
      .header-navigation-submenu__nested {
        left: auto;
        right: 100%;

        // to compensate for border-radius
        margin-right: -#{$header-submenu-border-radius * 2};
      }
    }
  }

  .header-navigation__list-item-link-wrapper {
    display: flex;
    align-items: center;

    &.active {
      height: 100%;
      border-bottom: 2px solid $c--main;
    }
  }

  .header-navigation__list-item-icon {
    margin-right: 6px;
  }

  .header-navigation__list-item,
  .header-navigation__list-link {
    &:hover {
      color: $c--main;
    }
  }

  .header-navigation__list-link {
    color: $c--white;

    // Make sure to update getNavItemWidthValue if the default font changes
    // as there are calculations that depend on it
    font-size: 16px;
    font-weight: bold;

    &:hover + .header-navigation__arrow {
      .svg-icon {
        fill: $c--main;
      }
    }
  }

  .header-navigation__arrow {
    margin-left: 5px;

    .svg-icon {
      fill: $c--white;
    }
  }
}
</style>
